<template>
    <div class="page-container">
        <div class="settings-form">
            <div class="header-section">
                <h2 class="page-title">百度广告账号管理</h2>

                <div class="license-info">
                    <template v-if="hasSmartCardZt">
                        <div class="license-item">
                            <span class="label">总授权数：</span>
                            <span class="value">{{ licenseInfo.total === -1 ? '无限制' : licenseInfo.total }}</span>
                        </div>
                        <div class="license-item">
                            <span class="label">已使用：</span>
                            <span class="value">{{ licenseInfo.used }}</span>
                        </div>
                        <div class="license-item">
                            <span class="label">剩余可用：</span>
                            <span class="value">{{ licenseInfo.remaining === -1 ? '无限制' : licenseInfo.remaining
                            }}</span>
                        </div>
                    </template>
                    <template v-else>
                        <div class="license-item">
                            <span class="label">当前模式：</span>
                            <span class="value">单主体模式</span>
                        </div>
                        <div class="license-item">
                            <span class="label">总主体数：</span>
                            <span class="value">1</span>
                        </div>
                    </template>
                </div>
            </div>

            <!-- 搜索区域 -->
            <div class="search-bar">
                <el-input v-model="searchForm.bd_name" placeholder="请输入百度广告名称" clearable size="large"
                    style="width: 300px; margin-right: 15px" @keyup.enter="handleSearch">
                    <template #prefix>
                        <el-icon>
                            <Search />
                        </el-icon>
                    </template>
                </el-input>

                <el-button type="primary" size="large" @click="handleSearch">
                    <el-icon>
                        <Search />
                    </el-icon>搜索
                </el-button>

                <el-button type="success" size="large" @click="openAddDialog" style="float: right">
                    <el-icon>
                        <Plus />
                    </el-icon>新增百度广告
                </el-button>

                <el-button type="primary" size="large" @click="openOnlineAuthDialog" class="online-auth-btn">
                    <el-icon>
                        <Key />
                    </el-icon>在线授权
                </el-button>
            </div>

            <!-- 账号列表 -->
            <el-table :data="accountList" stripe style="width: 100%; margin-top: 20px" v-loading="loading" row-key="id"
                :row-class-name="getRowClassName">
                <el-table-column prop="bd_name" label="百度广告名称" width="180" />

                <el-table-column label="关联用户" width="150">
                    <template #default="{ row }">
                        <div class="user-info">
                            <div class="user-nickname">{{ getUserInfo(row.user_id).nickname }}</div>
                            <div class="user-phone">{{ getUserInfo(row.user_id).phone }}</div>
                        </div>
                    </template>
                </el-table-column>

                <el-table-column label="账户信息" width="200">
                    <template #default="{ row }">
                        <div class="account-info">
                            <div class="account-name">{{ row.ucname || '-' }}</div>
                            <div class="account-id">UCID: {{ row.ucid || '-' }}</div>
                        </div>
                    </template>
                </el-table-column>

                <el-table-column label="账户类型" width="100">
                    <template #default="{ row }">
                        <el-tag
                            :type="row.user_acct_type === 2 ? 'warning' : (row.user_acct_type === 4 ? 'danger' : 'info')"
                            size="small">
                            {{ getUserAcctTypeLabel(row.user_acct_type) }}
                        </el-tag>
                    </template>
                </el-table-column>

                <el-table-column label="授权状态" width="120">
                    <template #default="{ row }">
                        <el-tag :type="row.auth_status === 1 ? 'success' : 'danger'" size="small">
                            {{ row.auth_status === 1 ? '正常' : '失效' }}
                        </el-tag>
                        <el-tag v-if="row.is_expired" type="warning" size="small" class="ml-1">即将过期</el-tag>
                    </template>
                </el-table-column>

                <el-table-column label="Access Token" width="200">
                    <template #default="{ row }">
                        <span v-if="row.access_token">{{ row.access_token.substring(0, 30) }}...</span>
                        <span v-else class="empty-value">未获取</span>
                    </template>
                </el-table-column>

                <el-table-column label="Token有效期" width="150">
                    <template #default="{ row }">
                        <div class="token-expire-info">
                            <div>剩余: {{ formatExpireTime(row.expire_in_seconds) }}</div>
                            <div class="refresh-time">刷新: {{ row.last_refresh_time || '-' }}</div>
                        </div>
                    </template>
                </el-table-column>

                <el-table-column label="到期时间" width="150">
                    <template #default="{ row }">
                        <div>{{ row.expires_time || '-' }}</div>
                    </template>
                </el-table-column>

                <el-table-column prop="create_time" label="创建时间" width="180" />
                <el-table-column prop="update_time" label="更新时间" width="180" />

                <el-table-column label="操作" width="240" fixed="right">
                    <template #default="scope">
                        <el-button type="primary" size="small" @click="openEditDialog(scope.row)">
                            <el-icon>
                                <Edit />
                            </el-icon>编辑
                        </el-button>
                        <el-button type="danger" size="small" @click="handleDelete(scope.row.id)">
                            <el-icon>
                                <Delete />
                            </el-icon>删除
                        </el-button>
                        <el-button type="info" size="small" @click="refreshToken(scope.row)"
                            :loading="refreshLoading[scope.row.id]">
                            <el-icon>
                                <Refresh />
                            </el-icon>刷新
                        </el-button>
                    </template>
                </el-table-column>
            </el-table>

            <!-- 分页组件 -->
            <div class="pagination-container">
                <el-pagination v-model:current-page="pagination.page" v-model:page-size="pagination.pageSize"
                    :total="pagination.total" :page-sizes="[10, 20, 30, 50]"
                    layout="total, sizes, prev, pager, next, jumper" @size-change="fetchAccountList"
                    @current-change="fetchAccountList" />
            </div>
        </div>

        <!-- 在线授权对话框 -->
        <el-dialog v-model="onlineAuthDialogVisible" title="百度广告在线授权" width="680px" class="online-auth-dialog-wrapper">
            <div class="online-auth-dialog">
                <div class="auth-type-section">
                    <h3 class="section-title">百度账号授权</h3>
                    <div class="auth-type-buttons">
                        <div class="auth-type-item active">
                            <div class="auth-icon-wrapper">
                                <div class="auth-icon baidu-icon">🔍</div>
                                <div class="selected-indicator">
                                    <el-icon>
                                        <Check />
                                    </el-icon>
                                </div>
                            </div>
                            <div class="auth-text">
                                <div class="auth-title">百度推广账号授权</div>
                                <div class="auth-desc">使用百度推广账号进行安全授权</div>
                                <div class="auth-features">
                                    <span class="feature-tag">快速</span>
                                    <span class="feature-tag">安全</span>
                                    <span class="feature-tag">稳定</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="permission-section">
                    <h3 class="section-title">授权权限说明</h3>
                    <div class="permission-list">
                        <div class="permission-item">
                            <el-icon color="#2B6BFF">
                                <Check />
                            </el-icon>
                            <span>财务信息 - 余额、流水、消费等</span>
                        </div>
                        <div class="permission-item">
                            <el-icon color="#2B6BFF">
                                <Check />
                            </el-icon>
                            <span>账号信息 - 账户详情、预算等</span>
                        </div>
                        <div class="permission-item">
                            <el-icon color="#2B6BFF">
                                <Check />
                            </el-icon>
                            <span>物料管理 - 关键词、创意、计划等</span>
                        </div>
                        <div class="permission-item">
                            <el-icon color="#2B6BFF">
                                <Check />
                            </el-icon>
                            <span>数据报表 - 多维度报表数据</span>
                        </div>
                        <div class="permission-item">
                            <el-icon color="#2B6BFF">
                                <Check />
                            </el-icon>
                            <span>人群管理 - DMP人群包管理</span>
                        </div>
                    </div>
                </div>

                <div class="tips-section">
                    <div class="tips-header">
                        <el-icon class="tips-icon">
                            <InfoFilled />
                        </el-icon>
                        <span>温馨提示</span>
                    </div>
                    <div class="tips-content">
                        <p>• Access Token有效期24小时，Refresh Token有效期30天</p>
                        <p>• 授权成功后系统将自动创建广告账号</p>
                        <p>• 可随时在百度推广平台撤销授权</p>
                        <p>• 超管账户授权后，其子账户均可使用该Token</p>
                    </div>
                </div>
            </div>

            <template #footer>
                <div class="dialog-footer">
                    <el-button @click="onlineAuthDialogVisible = false" size="large" class="cancel-btn">取消授权</el-button>
                    <el-button type="primary" @click="startOnlineAuth" :loading="authLoading" size="large"
                        class="confirm-btn">
                        <template #loading><el-icon class="is-loading">
                                <Loading />
                            </el-icon>授权中...</template>
                        <template #default><el-icon>
                                <Key />
                            </el-icon>开始授权</template>
                    </el-button>
                </div>
            </template>
        </el-dialog>

        <!-- 新增/编辑对话框 -->
        <el-dialog v-model="dialogVisible" :title="dialogTitle" width="600px" :close-on-click-modal="false">
            <el-form ref="accountForm" :model="accountForm" label-width="120px" :rules="formRules">
                <el-form-item label="广告名称" prop="bd_name">
                    <el-input v-model="accountForm.bd_name" placeholder="请输入百度广告名称" size="large" />
                </el-form-item>
                <el-form-item label="账户UCID">
                    <el-input v-model="accountForm.ucid" placeholder="请输入账户UCID（可选）" size="large" />
                </el-form-item>
                <el-form-item label="账户名称">
                    <el-input v-model="accountForm.ucname" placeholder="请输入账户名称（可选）" size="large" />
                </el-form-item>
                <el-form-item label="备注信息">
                    <el-input v-model="accountForm.notes" placeholder="请输入备注信息" type="textarea" :rows="3" />
                </el-form-item>
            </el-form>
            <template #footer>
                <el-button @click="dialogVisible = false">取消</el-button>
                <el-button type="primary" @click="submitAccountForm">确认</el-button>
            </template>
        </el-dialog>
    </div>
</template>

<script>
import axios from 'axios';
import config from '@/config.js';
import Cookies from 'js-cookie';
import {
    Search, Plus, Edit, Delete, Loading, Refresh, Check, Key, InfoFilled
} from '@element-plus/icons-vue'

export default {
    name: 'BdAccountManagement',
    components: { Search, Plus, Edit, Delete, Loading, Refresh, Check, Key, InfoFilled },
    data() {
        return {
            loading: false,
            refreshLoading: {},
            onlineAuthDialogVisible: false,
            authLoading: false,
            dialogVisible: false,
            dialogTitle: '新增百度广告',
            isEditMode: false,
            searchForm: { bd_name: '' },
            accountForm: {
                id: null,
                bd_name: '',
                ucid: '',
                ucname: '',
                notes: ''
            },
            formRules: {
                bd_name: [{ required: true, message: '请输入百度广告名称', trigger: 'blur' }]
            },
            accountList: [],
            pagination: { page: 1, pageSize: 10, total: 0 },
            hasSmartCardZt: false,
            licenseInfo: { total: 0, used: 0, remaining: 0 },
            userList: [],
            userMap: {},
            userLoading: false
        }
    },
    computed: {
        userInfo() {
            return JSON.parse(Cookies.get('user_info') || '{}')
        }
    },
    mounted() {
        this.fetchAccountList();
        this.fetchLicenseInfo();
        this.checkSmartCardZt();
        this.fetchUserList();
        this.setupMessageListener();
    },

    methods: {
        getRowClassName({ row }) {
            return `bd-row-${row.id}`;
        },

        getUserAcctTypeLabel(type) {
            const typeMap = {
                1: '普通账户',
                2: '超管账户',
                4: '代理商账户'
            };
            return typeMap[type] || '未知';
        },

        getUserInfo(userId) {
            if (!userId) return { nickname: '未分配', phone: '' };
            const user = this.userMap[userId];
            if (user) return { nickname: user.nickname, phone: user.phone };
            return { nickname: '用户已删除', phone: '' };
        },

        async fetchUserList() {
            this.userLoading = true;
            try {
                const response = await axios.post(`${config.BASE_URL}/shop/api/business_card_management.php`, {
                    action: 'get_users',
                    store_id: this.userInfo.id
                });
                if (response.data.data) {
                    this.userList = response.data.data;
                    this.userMap = {};
                    response.data.data.forEach(user => { this.userMap[user.id] = user; });
                }
            } catch (error) {
                console.error('获取用户列表错误:', error);
            } finally {
                this.userLoading = false;
            }
        },

        setupMessageListener() {
            window.addEventListener('message', (event) => {
                const allowedOrigins = ['https://bd-hd.aijzdi.com', window.location.origin];
                if (!allowedOrigins.includes(event.origin)) return;
                if (event.data.type === 'BD_OAUTH_SUCCESS') {
                    this.$message.success('授权成功，正在刷新页面...');
                    setTimeout(() => this.fetchAccountList(), 1000);
                }
            });
        },

        formatExpireTime(seconds) {
            if (!seconds || seconds <= 0) return '已过期';
            const hours = Math.floor(seconds / 3600);
            const minutes = Math.floor((seconds % 3600) / 60);
            return hours > 0 ? `${hours}小时${minutes}分钟` : `${minutes}分钟`;
        },

        async fetchAccountList() {
            this.loading = true;
            try {
                const params = {
                    action: this.searchForm.bd_name ? 'search' : 'list',
                    store_id: this.userInfo.id,
                    page: this.pagination.page,
                    page_size: this.pagination.pageSize
                };
                if (this.searchForm.bd_name) params.bd_name = this.searchForm.bd_name;

                const { data } = await axios.post(`${config.BASE_URL}/shop/api/bd_auth_management.php`, params);
                if (data.data) {
                    this.accountList = data.data;
                    this.pagination.total = data.pagination?.total || data.data.length;
                }
            } catch (error) {
                this.$message.error('获取账号列表失败');
            } finally {
                this.loading = false;
            }
        },

        async fetchLicenseInfo() {
            try {
                const response = await axios.post(`${config.BASE_URL}/shop/api/bd_auth_management.php`, {
                    action: 'license_info',
                    store_id: this.userInfo.id
                });
                if (response.data && !response.data.error) {
                    this.licenseInfo = response.data;
                }
            } catch (error) {
                console.error('获取授权信息失败:', error);
            }
        },

        async checkSmartCardZt() {
            try {
                const response = await axios.post(`${config.BASE_URL}/shop/api/bd_auth_management.php`, {
                    action: 'check_smart_card_zt',
                    store_id: this.userInfo.id
                });
                if (response.data.success) {
                    this.hasSmartCardZt = response.data.has_smart_card_zt;
                }
            } catch (error) {
                this.hasSmartCardZt = false;
            }
        },

        handleSearch() {
            this.pagination.page = 1;
            this.fetchAccountList();
        },

        openAddDialog() {
            if (!this.hasSmartCardZt) {
                if (this.accountList.length >= 1) {
                    this.$message.warning('单主体模式下只能添加1个百度账号！');
                    return;
                }
            } else {
                if (this.licenseInfo.remaining <= 0 && this.licenseInfo.total !== -1) {
                    this.$message.warning('授权账号数已满，请联系管理员充值！');
                    return;
                }
            }
            this.dialogTitle = '新增百度广告';
            this.isEditMode = false;
            this.accountForm = {
                id: null,
                bd_name: '',
                ucid: '',
                ucname: '',
                notes: ''
            };
            this.dialogVisible = true;
        },

        openEditDialog(row) {
            this.dialogTitle = '编辑百度广告';
            this.isEditMode = true;
            this.accountForm = {
                id: row.id,
                bd_name: row.bd_name,
                ucid: row.ucid || '',
                ucname: row.ucname || '',
                notes: row.notes || ''
            };
            this.dialogVisible = true;
        },

        async submitAccountForm() {
            this.$refs.accountForm.validate(async (valid) => {
                if (valid) {
                    try {
                        const action = this.isEditMode ? 'update' : 'add';
                        const payload = { ...this.accountForm, store_id: this.userInfo.id };
                        const response = await axios.post(`${config.BASE_URL}/shop/api/bd_auth_management.php`, { action, ...payload });
                        if (response.data.error) {
                            this.$message.error(response.data.error);
                        } else if (response.data.success) {
                            this.$message.success(this.isEditMode ? '更新成功' : '添加成功');
                            this.dialogVisible = false;
                            this.fetchAccountList();
                        }
                    } catch (error) {
                        this.$message.error('操作失败');
                    }
                }
            });
        },

        async handleDelete(id) {
            this.$confirm('确定要删除该百度广告账号吗？删除后所有相关数据将被清除，确定删除吗？', '警告', {
                confirmButtonText: '确认删除',
                cancelButtonText: '取消',
                type: 'warning'
            }).then(async () => {
                try {
                    const response = await axios.post(`${config.BASE_URL}/shop/api/bd_auth_management.php`, {
                        action: 'delete',
                        id: id,
                        store_id: this.userInfo.id
                    });
                    if (response.data.success) {
                        this.$message.success('删除成功');
                        this.fetchAccountList();
                    } else {
                        this.$message.error(response.data.error || '删除失败');
                    }
                } catch (error) {
                    this.$message.error('删除失败');
                }
            }).catch(() => { });
        },

        async refreshToken(row) {
            this.refreshLoading = { ...this.refreshLoading, [row.id]: true };
            try {
                const response = await axios.post(`${config.BASE_URL}/shop/api/bd_auth_management.php`, {
                    action: 'refresh_token',
                    id: row.id,
                    store_id: this.userInfo.id
                });
                if (response.data.success) {
                    this.$message.success('Token刷新成功');
                    this.fetchAccountList();
                } else if (response.data.need_reauth) {
                    this.$message.error('授权已过期，请重新授权');
                    this.openOnlineAuthDialog();
                } else {
                    this.$message.error(response.data.message || '刷新失败');
                }
            } catch (error) {
                this.$message.error('刷新Token失败');
            } finally {
                this.refreshLoading = { ...this.refreshLoading, [row.id]: false };
            }
        },

        openOnlineAuthDialog() {
            if (!this.hasSmartCardZt) {
                if (this.accountList.length >= 1) {
                    this.$message.warning('单主体模式下只能添加1个百度账号！');
                    return;
                }
            } else {
                if (this.licenseInfo.remaining <= 0 && this.licenseInfo.total !== -1) {
                    this.$message.warning('授权账号数已满，请联系管理员充值！');
                    return;
                }
            }
            this.onlineAuthDialogVisible = true;
            this.authLoading = false;
        },

        async startOnlineAuth() {
            this.authLoading = true;
            try {
                const initialAccountCount = this.accountList.length;
                const authParams = {
                    store_id: this.userInfo.id,
                    base_url: config.BASE_URL,
                    auth_type: 'bd',
                    timestamp: Date.now(),
                    initial_count: initialAccountCount
                };
                const state = encodeURIComponent(JSON.stringify(authParams));
                const authUrl = `https://bd-hd.aijzdi.com/?state=${state}`;
                const authWindow = window.open(authUrl, 'baidu_oauth', 'width=800,height=700');
                if (!authWindow) {
                    this.$message.error('请允许弹出窗口以进行授权');
                    return;
                }
                this.checkAuthCompletion(initialAccountCount);
                this.onlineAuthDialogVisible = false;
                this.$message.success('已打开授权页面，请在新窗口中完成授权');
            } catch (error) {
                this.$message.error('启动授权失败');
            } finally {
                this.authLoading = false;
            }
        },

        async checkAuthCompletion(initialCount) {
            let checkCount = 0;
            const maxCheckCount = 60;
            const checkInterval = setInterval(async () => {
                try {
                    checkCount++;
                    const authStatus = await this.checkAuthStatusFromBackend(initialCount);
                    if (authStatus.status === 'completed') {
                        clearInterval(checkInterval);
                        this.handleAuthSuccess(authStatus);
                    } else if (authStatus.status === 'failed') {
                        clearInterval(checkInterval);
                        this.$message.error(`授权失败: ${authStatus.message}`);
                    }
                    if (checkCount >= maxCheckCount) {
                        clearInterval(checkInterval);
                        this.$message.warning('授权检测超时，请检查授权状态');
                    }
                } catch (error) {
                    if (checkCount >= 10) {
                        clearInterval(checkInterval);
                        this.$message.error('授权状态检查异常');
                    }
                }
            }, 3000);
        },

        async checkAuthStatusFromBackend(initialCount) {
            try {
                const response = await axios.post(`${config.BASE_URL}/shop/api/bd_auth_management.php`, {
                    action: 'check_oauth_status',
                    store_id: this.userInfo.id,
                    initial_count: initialCount
                });
                return response.data;
            } catch (error) {
                return { status: 'error', message: '检查失败' };
            }
        },

        handleAuthSuccess(authStatus) {
            let successMessage = '授权成功！';
            if (authStatus.new_accounts && authStatus.new_accounts.length > 0) {
                const accountNames = authStatus.new_accounts.map(acc => acc.bd_name).join('、');
                successMessage = `授权成功！新账号「${accountNames}」已创建`;
            }
            this.$message.success(successMessage);
            setTimeout(() => {
                this.fetchAccountList();
                this.tryCloseAuthWindow();
            }, 1000);
        },

        tryCloseAuthWindow() {
            if (window.opener && !window.opener.closed) window.opener.focus();
            this.$nextTick(() => {
                this.$notify({ title: '授权完成', message: '账号列表已自动刷新', type: 'success', duration: 3000 });
            });
        }
    }
}
</script>

<style scoped lang="scss">
.page-container {
    padding: 20px;
}

.header-section {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
}

.license-info {
    display: flex;
    gap: 20px;
    background: linear-gradient(135deg, rgba(43, 107, 255, 0.1) 0%, rgba(0, 166, 255, 0.1) 100%);
    padding: 10px 20px;
    border-radius: 8px;
    font-size: 14px;
    border: 1px solid rgba(43, 107, 255, 0.2);
}

.license-item {
    display: flex;
    align-items: center;

    .label {
        color: #606266;
        font-weight: 500;
    }

    .value {
        font-weight: 600;
        color: #2B6BFF;
    }
}

.settings-form {
    max-width: 95%;
    margin: 0 auto;
    padding: 32px 40px;
    background: #ffffff;
    border-radius: 12px;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
}

.page-title {
    font-size: 24px;
    color: #1f2937;
    margin-bottom: 32px;
    font-weight: 600;
    text-align: center;
}

.search-bar {
    display: flex;
    align-items: center;
    margin-bottom: 24px;
    padding: 16px;
    background: #f8fafc;
    border-radius: 8px;
}

.pagination-container {
    margin-top: 24px;
    display: flex;
    justify-content: flex-end;
}

.user-info {
    .user-nickname {
        font-weight: 500;
        margin-bottom: 4px;
    }

    .user-phone {
        color: #909399;
        font-size: 12px;
    }
}

.account-info {
    .account-name {
        font-weight: 500;
        margin-bottom: 4px;
    }

    .account-id {
        color: #909399;
        font-size: 12px;
    }
}

.token-expire-info {
    font-size: 12px;

    .refresh-time {
        color: #909399;
    }
}

.empty-value {
    color: #909399;
    font-style: italic;
}

.ml-1 {
    margin-left: 4px;
}

.online-auth-dialog-wrapper {
    :deep(.el-dialog__header) {
        background: linear-gradient(135deg, #2B6BFF 0%, #00A6FF 100%);
        margin: 0;

        .el-dialog__title {
            color: white;
            font-size: 18px;
            font-weight: 600;
        }

        .el-dialog__headerbtn .el-dialog__close {
            color: white;
        }
    }
}

.online-auth-dialog {
    .section-title {
        color: #1f2937;
        font-size: 16px;
        font-weight: 600;
        margin-bottom: 20px;
        display: flex;
        align-items: center;

        &::before {
            content: '';
            display: inline-block;
            width: 4px;
            height: 16px;
            background: linear-gradient(135deg, #2B6BFF 0%, #00A6FF 100%);
            border-radius: 2px;
            margin-right: 8px;
        }
    }
}

.auth-type-buttons {
    width: 100%;
}

.auth-type-item {
    border: 2px solid #e8e8e8;
    border-radius: 12px;
    padding: 24px 20px;
    transition: all 0.3s ease;
    background: white;
    position: relative;
    overflow: hidden;

    &:hover {
        border-color: #2B6BFF;
        transform: translateY(-2px);
        box-shadow: 0 4px 20px rgba(43, 107, 255, 0.15);
    }

    &.active {
        border-color: #2B6BFF;
        background: rgba(43, 107, 255, 0.05);
    }
}

.baidu-icon {
    background: linear-gradient(135deg, #2B6BFF 0%, #00A6FF 100%);
    color: white;
    width: 60px;
    height: 60px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 28px;
}

.auth-icon-wrapper {
    position: relative;
    display: inline-block;
    margin-bottom: 16px;
}

.selected-indicator {
    position: absolute;
    top: -4px;
    right: -4px;
    width: 20px;
    height: 20px;
    background: #2B6BFF;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transform: scale(0);
    transition: all 0.3s ease;

    .el-icon {
        color: white;
        font-size: 12px;
    }
}

.active .selected-indicator {
    opacity: 1;
    transform: scale(1);
}

.auth-text {
    text-align: left;
}

.auth-title {
    font-size: 16px;
    font-weight: 600;
    color: #1f2937;
    margin-bottom: 6px;
}

.auth-desc {
    font-size: 13px;
    color: #666;
    margin-bottom: 12px;
}

.auth-features {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
}

.feature-tag {
    background: rgba(43, 107, 255, 0.1);
    color: #2B6BFF;
    padding: 2px 8px;
    border-radius: 10px;
    font-size: 11px;
    font-weight: 500;
}

.permission-section {
    margin: 30px 0;
    padding: 25px;
    background: #f8fafc;
    border-radius: 12px;
}

.permission-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.permission-item {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 14px;
    color: #333;
}

.tips-section {
    background: linear-gradient(135deg, #fffbf0 0%, #fef6e6 100%);
    border: 1px solid #ffe58f;
    border-radius: 8px;
    padding: 20px;
}

.tips-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;

    .tips-icon {
        color: #faad14;
    }

    span {
        font-weight: 600;
    }
}

.tips-content p {
    margin: 6px 0;
    color: #666;
    font-size: 13px;
}

.dialog-footer {
    display: flex;
    gap: 12px;
    justify-content: flex-end;
}

.cancel-btn {
    padding: 12px 24px;
    border: 1px solid #dcdfe6;

    &:hover {
        border-color: #2B6BFF;
        color: #2B6BFF;
    }
}

.confirm-btn {
    padding: 12px 32px;
    background: linear-gradient(135deg, #2B6BFF 0%, #00A6FF 100%);
    border: none;

    &:hover {
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(43, 107, 255, 0.3);
    }
}
</style>